// The user record as the mock-up draws it: one panel, four tabs, the save row
// on the bottom edge.
//
// This is the old AddUser modal re-laid-out. Every field name, validation rule
// and the whole handleFormSubmit body are unchanged, so adding and editing a
// user hits the same endpoints with the same payload as before. What is new is
// only presentation: tabs, the branch chip box in place of the multi-select,
// and the password helpers (show / generate / copy / strength), which act on
// the very same `password` input.
//
// Controls the server has no field for are drawn — the mock-up's full layout —
// disabled and tagged "needs backend". They are listed in
// docs/BACKEND-COMPLETE.md (Part 3). Nothing they show is ever sent.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import User from "../../../core/json/User";
import { UserBranchDetail } from "../../../core/json/UserBranchDetail";
import { addUser, updateuser } from "../../../services/AdminApiServices";
import { fetchUser, fetchUserByBranch } from "../../../redux/userServices";
import {
  AxAvatar,
  AxBranchIcon,
  AxCaret,
  AxClearIcon,
  AxFact,
  AxField,
  AxInfoIcon,
  AxPill,
  AxSectionHead,
  AxSelectField,
  AxSwitchRow,
  AxTabs,
  AxTodoNote,
  AxTodoTag,
} from "../ui/adminUi";

const TABS = [
  { key: "profile", label: "Profile" },
  { key: "access", label: "Access & login" },
  { key: "branches", label: "Branches" },
  { key: "activity", label: "Activity" },
];

// which tab each required field lives on, so a failed save can open it
const FIELD_TAB = {
  name: "profile",
  usercode: "profile",
  userrole: "profile",
  contact1: "profile",
  contact2: "profile",
  address1: "profile",
  address2: "profile",
  email: "profile",
  username: "access",
  password: "access",
};

const scorePassword = (v) => {
  let score = 0;
  if (v.length >= 8) score++;
  if (v.length >= 12) score++;
  if (/[A-Z]/.test(v) && /[a-z]/.test(v)) score++;
  if (/[0-9]/.test(v)) score++;
  if (/[^A-Za-z0-9]/.test(v)) score++;
  return score;
};

const generatePassword = () => {
  const sets = [
    "ABCDEFGHJKLMNPQRSTUVWXYZ",
    "abcdefghijkmnpqrstuvwxyz",
    "23456789",
    "!@#$%&*?",
  ];
  const out = sets.map((s) => s[Math.floor(Math.random() * s.length)]);
  const all = sets.join("");
  while (out.length < 14) out.push(all[Math.floor(Math.random() * all.length)]);
  return out.sort(() => Math.random() - 0.5).join("");
};

const UserDetail = ({ mode, data, handleClose, onDelete, canDelete }) => {
  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
    reset,
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const [branches, setbranches] = useState([]);
  const [defaultbranch, setdefaultbranch] = useState();
  const [tab, setTab] = useState("profile");
  const [branchPop, setBranchPop] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState("");
  const boxRef = useRef(null);

  const { userRoles } = useSelector((state) => state.usersetting);
  const { userId } = useSelector((state) => state.userauth);
  const { branch, branchlist } = useSelector((state) => state.branchlist);
  const dispatch = useDispatch();

  const branchList = useMemo(
    () => (branchlist || []).map((e) => ({ label: e?.name, value: e?.guid })),
    [branchlist]
  );

  /* ---- load the record, exactly as the old modal did ---- */
  useEffect(() => {
    if (mode === "edit" && data) {
      setValue("name", data.name);
      setValue("contact1", data.primaryContactNo);
      setValue("contact2", data.secondaryContactNo);
      setValue("address1", data.address1);
      setValue("address2", data.address2);
      setValue("address3", data.address3);
      setValue("userrole", data.userRole?.guid);
      setValue("username", data.userName);
      //   setValue("password", data.password);
      setValue("email", data.email);
      setValue("usercode", data.userCode);
      setValue("weblogin", data.canAccessWeb);
      const newlist = data?.assignedBranch?.map((e) => ({
        label: e.branch.name,
        value: e.branch.guid,
      }));
      setbranches(newlist);

      const DefaultBranch = data?.assignedBranch?.find((e) => e.isDefaultBranch == true);
      const newbranch = DefaultBranch
        ? DefaultBranch.branch
        : data?.assignedBranch?.[0]?.branch;
      if (newbranch) setdefaultbranch({ label: newbranch.name, value: newbranch.guid });
      else setdefaultbranch(undefined);
    } else {
      reset();
      setValue("userrole", "");
      setbranches([]);
      setdefaultbranch(undefined);
    }
    setTab("profile");
    setDirty(false);
    setMsg("");
    setShowPw(false);
  }, [data, mode]);

  /* ---- the chip box closes when you click away ---- */
  useEffect(() => {
    if (!branchPop) return undefined;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setBranchPop(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [branchPop]);

  /* ---- save: unchanged from the modal ---- */
  const handleFormSubmit = async (formData) => {
    const AssignedBranch = branchlist?.filter((e) =>
      branches?.map((b) => b.value)?.includes(e.guid)
    );

    const assgnBranches = AssignedBranch?.[0] ? AssignedBranch : [branch];
    const defBranch = branchlist?.find((e) => defaultbranch?.value == e.guid) || branch;

    const userObj = new User();
    const userclass = new User();

    for (const row of assgnBranches) {
      const branchdetailObj = new UserBranchDetail();
      branchdetailObj.User = userclass;
      branchdetailObj.Branch = row; // UserBranchDetail has `Branch`; `branch` sent both casings (G-15)
      branchdetailObj.IsDefaultBranch = row?.guid == defBranch.guid ? true : false;
      userObj.AssignedBranch.push(branchdetailObj);
    }
    userObj.id = data.id;
    userObj.guid = data.guid;
    userObj.Name = formData.name;
    userObj.PrimaryContactNo = formData.contact1;
    userObj.SecondaryContactNo = formData.contact2;
    userObj.Address1 = formData.address1;
    userObj.Address2 = formData.address2;
    userObj.Address3 = formData.address3;
    userObj.UserRole = userRoles?.find((e) => formData.userrole == e.guid); // UserRole Class
    userObj.UserName = formData.username;
    userObj.Password = formData.password;
    userObj.Email = formData.email;
    userObj.UserCode = formData.usercode;
    userObj.CanAccessWeb = formData.weblogin;
    userObj.Branch = defBranch;
    userObj.updatedUser = `{${userId?.id}}`;
    userObj.createdUser = `{${userId?.id}}`;

    try {
      const response =
        mode === "edit" ? await updateuser(userObj) : await addUser(userObj);

      if (response == true) {
        toast.success("Success");
        dispatch(fetchUserByBranch(branch.guid));
        dispatch(fetchUser());
        handleClose();
      }
    } catch (error) {
      toast.error(error?.response?.data?.Message);
      console.error("Error handling Account Type:", error);
    }
  };

  // a failed save opens the tab holding the first complaint
  const onInvalid = (errs) => {
    const first = Object.keys(errs)[0];
    if (first && FIELD_TAB[first]) setTab(FIELD_TAB[first]);
    setMsg("Check the fields marked in red");
  };

  /* ---- branches ---- */
  const touch = () => {
    setDirty(true);
    setMsg("");
  };
  const toggleBranch = (opt, on) => {
    const next = on
      ? [...(branches || []), opt]
      : (branches || []).filter((b) => b.value !== opt.value);
    setbranches(next);
    if (!next.length) setdefaultbranch(undefined);
    else if (!next.some((b) => b.value === defaultbranch?.value)) setdefaultbranch(next[0]);
    touch();
  };
  const removeBranch = (value) => {
    const next = (branches || []).filter((b) => b.value !== value);
    setbranches(next);
    if (defaultbranch?.value === value) setdefaultbranch(next[0]);
    touch();
  };

  /* ---- password helpers (client side only) ---- */
  const pw = watch("password") || "";
  const pwScore = scorePassword(pw);
  const pwClass = pwScore <= 2 ? "weak" : pwScore <= 3 ? "fair" : "good";
  const pwLabel = pwScore <= 2 ? "Weak" : pwScore <= 3 ? "Fair" : "Strong";

  const name = watch("name") || "";
  const usercode = watch("usercode") || "";
  const roleGuid = watch("userrole");
  const roleName = userRoles?.find((e) => e.guid == roleGuid)?.name;

  const errorKeys = Object.keys(errors);
  const tabHasError = (key) => errorKeys.some((k) => FIELD_TAB[k] === key);

  const subBits = [];
  if (roleName) subBits.push(roleName);
  if (usercode) subBits.push("Code " + usercode);
  subBits.push((branches?.length || 0) + " branch" + (branches?.length === 1 ? "" : "es"));
  if (defaultbranch?.label) subBits.push("Default " + defaultbranch.label);

  return (
    <form
      className="axa-detailform"
      onSubmit={handleSubmit(handleFormSubmit, onInvalid)}
      onChange={touch}
    >
      {/* ============ HEADER ============ */}
      <div className="axa-dhead">
        <AxAvatar name={name || "new"} />
        <div className="axa-dtitle">
          <div className="t">
            <span className="nm">{name || (mode === "edit" ? "User" : "New user")}</span>
          </div>
          <div className="s">{subBits.join(" · ")}</div>
        </div>
        <AxPill state={mode === "edit" ? (dirty ? "dirty" : "saved") : "new"} />
      </div>

      <AxTabs
        tabs={TABS.map((t) => ({
          ...t,
          count: t.key === "branches" ? branches?.length || 0 : undefined,
          warn: tabHasError(t.key) || (t.key === "branches" && !branches?.length),
        }))}
        active={tab}
        onChange={setTab}
      />

      <div className="axa-tabbody">
        {/* ---------- PROFILE ---------- */}
        <div className={"axa-pane" + (tab === "profile" ? " show" : "")}>
          <AxSectionHead>Person</AxSectionHead>
          <div className="axa-formgrid">
            <AxField
              label="Name"
              required
              hint={errors.name?.type === "required" ? "Name is required" : undefined}
              bad
            >
              <input
                type="text"
                autoComplete="off"
                placeholder="Full name"
                className={errors.name ? "err" : ""}
                {...register("name", { required: true })}
              />
            </AxField>

            <AxField
              label="User code"
              required
              hint={errors.usercode?.type === "required" ? "User Code is required" : undefined}
              bad
            >
              <input
                type="text"
                autoComplete="off"
                placeholder="e.g. 1042"
                className={"mono" + (errors.usercode ? " err" : "")}
                {...register("usercode", { required: true })}
              />
            </AxField>

            <AxSelectField
              label="User role"
              required
              hint={errors.userrole?.type === "required" ? "User role is required" : undefined}
              {...register("userrole", { required: true })}
            >
              <option value="" key="" disabled>
                --select--
              </option>
              {userRoles?.map((e) => (
                <option value={e.guid} key={e.id}>
                  {e.name}
                </option>
              ))}
            </AxSelectField>

            <AxField
              label="Primary contact"
              required
              hint={
                errors.contact1?.type === "required" ? "Primary Contact is required" : undefined
              }
              bad
            >
              <input
                type="number"
                autoComplete="off"
                className={"mono" + (errors.contact1 ? " err" : "")}
                {...register("contact1", { required: true })}
              />
            </AxField>

            <AxField
              label="Secondary contact"
              required
              hint={
                errors.contact2?.type === "required"
                  ? "Secondary Contact is required"
                  : undefined
              }
              bad
            >
              <input
                type="number"
                autoComplete="off"
                className={"mono" + (errors.contact2 ? " err" : "")}
                {...register("contact2", { required: true })}
              />
            </AxField>

            <AxField
              label="Email"
              hint={
                errors?.email?.type === "pattern"
                  ? "Email is not valid"
                  : "Used for password resets and report subscriptions."
              }
              bad={errors?.email?.type === "pattern"}
            >
              <input
                type="text"
                autoComplete="off"
                placeholder="name@company.com"
                className={errors.email ? "err" : ""}
                {...register("email", {
                  required: false,
                  pattern:
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                })}
              />
            </AxField>
          </div>

          <AxSectionHead>Address</AxSectionHead>
          <div className="axa-formgrid">
            <AxField
              label="Address 1"
              required
              hint={errors.address1?.type === "required" ? "Address 1 is required" : undefined}
              bad
            >
              <input
                type="text"
                autoComplete="off"
                className={errors.address1 ? "err" : ""}
                {...register("address1", { required: true })}
              />
            </AxField>
            <AxField
              label="Address 2"
              required
              hint={errors.address2?.type === "required" ? "Address 2 is required" : undefined}
              bad
            >
              <input
                type="text"
                autoComplete="off"
                className={errors.address2 ? "err" : ""}
                {...register("address2", { required: true })}
              />
            </AxField>
            <AxField label="Address 3">
              <input type="text" autoComplete="off" {...register("address3")} />
            </AxField>
          </div>
        </div>

        {/* ---------- ACCESS ---------- */}
        <div className={"axa-pane" + (tab === "access" ? " show" : "")}>
          <AxSectionHead>Sign in</AxSectionHead>
          <div className="axa-formgrid">
            <AxField
              label="User name"
              required
              hint={errors.username?.type === "required" ? "Username is required" : undefined}
              bad
            >
              <input
                type="text"
                autoComplete="off"
                placeholder="Used to sign in"
                className={"mono" + (errors.username ? " err" : "")}
                {...register("username", { required: true })}
              />
            </AxField>

            <AxField
              label="Password"
              required
              span={2}
              hint={
                errors.password?.type === "required"
                  ? "Password is required"
                  : mode === "edit"
                  ? "Editing a user asks for the password again — it is not read back from the server."
                  : undefined
              }
              bad={!!errors.password}
            >
              <div className="axa-pwwrap">
                <input
                  type={showPw ? "text" : "password"}
                  autoComplete="new-password"
                  className={errors.password ? "err" : ""}
                  {...register("password", { required: true })}
                />
                <button
                  type="button"
                  title="Show or hide"
                  onClick={() => setShowPw((s) => !s)}
                >
                  <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <path d="M1.6 8s2.4-4.2 6.4-4.2S14.4 8 14.4 8s-2.4 4.2-6.4 4.2S1.6 8 1.6 8z" />
                    <circle cx="8" cy="8" r="1.7" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setValue("password", generatePassword(), { shouldValidate: true });
                    setShowPw(true);
                    touch();
                    toast.success("Password generated — copy it before you save");
                  }}
                >
                  Generate
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!pw) {
                      toast.error("Nothing to copy yet");
                      return;
                    }
                    if (navigator.clipboard) {
                      navigator.clipboard.writeText(pw).then(
                        () => toast.success("Password copied"),
                        () => toast.error("Copy blocked by the browser")
                      );
                    } else toast.error("Copy blocked by the browser");
                  }}
                >
                  Copy
                </button>
              </div>
              {pw ? (
                <div className={"axa-pwmeter " + pwClass}>
                  <span className="b">
                    <i style={{ width: Math.min(100, pwScore * 20) + "%" }} />
                  </span>
                  <span className="t">{pwLabel}</span>
                </div>
              ) : null}
            </AxField>
          </div>

          <AxSectionHead>Where this account may sign in</AxSectionHead>
          <div className="axa-formgrid">
            <AxSwitchRow
              label="Enable web login"
              note="The browser application — masters, reports, administration."
              {...register("weblogin")}
            />
            <AxSwitchRow
              label="Enable POS / Windows login"
              note="The till application on the counter machines."
              todo
              disabled
            />
            <AxSwitchRow
              label="Account active"
              note="Switched off, the person keeps their history but cannot sign in."
              todo
              disabled
            />
          </div>

          <AxSectionHead>Security</AxSectionHead>
          <div className="axa-formgrid">
            <AxSwitchRow
              label="Must change password at next sign in"
              note="Recommended whenever you set a password for somebody else."
              todo
              disabled
            />
            <AxField label="Password expires" className="sel" todo>
              <select disabled defaultValue="0">
                <option value="0">Never</option>
                <option value="30">Every 30 days</option>
                <option value="60">Every 60 days</option>
                <option value="90">Every 90 days</option>
              </select>
              <AxCaret />
            </AxField>
            <AxField label="Session" todo>
              <button type="button" className="axa-btn axa-btn-ghost" disabled style={{ height: 34 }}>
                Sign this user out everywhere
              </button>
            </AxField>
          </div>

          <AxTodoNote>
            The POS login, active, must-change, expiry and sign-out controls above are part of
            the new design but have nothing to save to yet — see docs/BACKEND-COMPLETE.md (Part 3).
            They are disabled so nobody sets one and expects it to stick.
          </AxTodoNote>
        </div>

        {/* ---------- BRANCHES ---------- */}
        <div className={"axa-pane" + (tab === "branches" ? " show" : "")}>
          <AxSectionHead>
            Branches this user may work in <span className="req">*</span>
          </AxSectionHead>
          <div className="axa-chipbox" ref={boxRef}>
            {branches?.length ? (
              branches.map((b) => {
                const isDef = b.value === defaultbranch?.value;
                return (
                  <span key={b.value} className={"axa-bchip" + (isDef ? " def" : "")}>
                    {b.label}
                    {isDef ? <span className="star">default</span> : null}
                    <button
                      type="button"
                      aria-label={"Remove " + b.label}
                      onClick={() => removeBranch(b.value)}
                    >
                      <AxClearIcon />
                    </button>
                  </span>
                );
              })
            ) : (
              <span className="axa-chipempty">
                No branch yet — this user falls back to the branch you are signed in to.
              </span>
            )}
            <button
              type="button"
              className="axa-chipadd"
              onClick={(e) => {
                e.stopPropagation();
                setBranchPop((p) => !p);
              }}
            >
              + Add branch
            </button>

            {branchPop && (
              <div className="axa-chippop">
                {branchList.map((o) => (
                  <label key={o.value}>
                    <input
                      type="checkbox"
                      checked={!!branches?.some((b) => b.value === o.value)}
                      onChange={(e) => toggleBranch(o, e.target.checked)}
                    />
                    {o.label}
                  </label>
                ))}
                <div className="axa-chipfoot">
                  <button
                    type="button"
                    onClick={() => {
                      setbranches(branchList);
                      if (!defaultbranch) setdefaultbranch(branchList[0]);
                      touch();
                    }}
                  >
                    All branches
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setbranches([]);
                      setdefaultbranch(undefined);
                      touch();
                    }}
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="axa-formgrid" style={{ marginTop: 12 }}>
            <AxField
              label="Default branch"
              required
              className="sel"
              hint="The branch that loads when they sign in."
            >
              <select
                value={defaultbranch?.value || ""}
                disabled={!branches?.length}
                onChange={(e) => {
                  setdefaultbranch(branches.find((b) => b.value === e.target.value));
                  touch();
                }}
              >
                {branches?.length ? (
                  branches.map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.label}
                    </option>
                  ))
                ) : (
                  <option value="">Add a branch first</option>
                )}
              </select>
              <AxCaret />
            </AxField>
          </div>

          <AxSectionHead>
            Transfer history <AxTodoTag />
          </AxSectionHead>
          <div className="axa-emptyline">
            Branch moves are not recorded anywhere yet, so there is nothing to show. Use the
            Branch Transfer button on the page head to move people between branches.
          </div>
        </div>

        {/* ---------- ACTIVITY ---------- */}
        <div className={"axa-pane" + (tab === "activity" ? " show" : "")}>
          <AxSectionHead>
            At a glance <AxTodoTag />
          </AxSectionHead>
          <div className="axa-facts">
            <AxFact k="Last sign in" v="—" />
            <AxFact k="Open sessions" v="—" />
            <AxFact k="Failed attempts" v="—" />
            <AxFact k="Created" v="—" />
          </div>

          <AxSectionHead>
            Recent activity <AxTodoTag />
          </AxSectionHead>
          <div className="axa-emptyline">
            Nothing to show. Sign-ins, failed attempts and profile changes are not returned by
            the user endpoints today.
          </div>

          <AxTodoNote>
            This whole tab is the mock-up&apos;s layout waiting on an audit / sign-in-history
            endpoint — see docs/BACKEND-COMPLETE.md (Part 3).
          </AxTodoNote>
        </div>
      </div>

      {/* ============ FOOTER ============ */}
      <div className="axa-dfoot">
        <span className={"msg" + (msg ? " err" : "")}>{msg}</span>
        <span className="sp" />
        {mode === "edit" && canDelete ? (
          <button
            type="button"
            className="axa-btn axa-btn-danger"
            onClick={() => onDelete && onDelete(data)}
          >
            Delete
          </button>
        ) : null}
        <button type="button" className="axa-btn axa-btn-ghost" onClick={handleClose}>
          {mode === "edit" ? "Close" : "Cancel"}
        </button>
        <button type="submit" className="axa-btn axa-btn-primary">
          {mode === "edit" ? "Save changes" : "Save"}
        </button>
      </div>
    </form>
  );
};

export default UserDetail;
